import { Link } from 'react-router-dom'
import { Trophy, Flame } from 'lucide-react'

interface GameParticipant {
  id: string
  user_id: string
  team: 'red' | 'blue'
  role: string
  user: {
    id: string
    display_name: string
  }
}

interface HighlightedGameProps {
  game: {
    id: string
    date: string
    game_type: '1v1' | '2v2'
    final_red_score: number
    final_blue_score: number
    winner?: 'red' | 'blue'
    ai_summary?: string
    highlight_reason: string
    participants?: GameParticipant[]
  } | null
}

export function HighlightedGame({ game }: HighlightedGameProps) {
  if (!game) {
    return null
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Consistent orange/red color scheme for all highlighted games
  const style = {
    icon: <Flame className="h-6 w-6" />,
    gradient: 'from-orange-500 to-red-500',
    bg: 'bg-orange-50',
    border: 'border-orange-200'
  }

  const getParticipantsByTeam = (team: 'red' | 'blue') => {
    return game.participants
      ?.filter(p => p.team === team)
      .map(p => p.user.display_name)
      .join(', ') || 'Unknown'
  }

  return (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden border-2 ${style.border}`}>
      {/* Header with gradient */}
      <div className={`bg-gradient-to-r ${style.gradient} p-4`}>
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              {style.icon}
            </div>
            <div>
              <h3 className="text-lg font-bold">Game of the Day</h3>
              <p className="text-sm opacity-90">{formatDate(game.date)}</p>
            </div>
          </div>
          <Trophy className="h-8 w-8 opacity-75" />
        </div>
      </div>

      <Link to={`/games/${game.id}`} className="block">
        <div className={`p-6 ${style.bg} hover:opacity-90 transition-opacity`}>
          {/* AI Summary or Highlight Reason */}
          <div className="mb-4 text-center">
            {game.ai_summary ? (
              <div>
                <p className="text-xs font-medium text-purple-600 mb-2">💬 Selena AI</p>
                <p className="text-lg font-bold text-gray-900 italic">{game.ai_summary}</p>
              </div>
            ) : (
              <p className="text-lg font-bold text-gray-900">{game.highlight_reason}</p>
            )}
          </div>

          {/* Score Display */}
          <div className="flex items-center justify-center space-x-8 mb-4">
            <div className={`text-center p-4 rounded-lg ${game.winner === 'red' ? 'bg-red-100 border-2 border-red-300' : 'bg-white'}`}>
              <div className="text-sm font-medium text-red-600 mb-1">RED</div>
              <div className="text-4xl font-bold text-gray-900">{game.final_red_score}</div>
              {game.participants && (
                <div className="text-xs text-gray-600 mt-2">
                  {getParticipantsByTeam('red')}
                </div>
              )}
            </div>
            
            <div className="text-2xl font-bold text-gray-400">-</div>
            
            <div className={`text-center p-4 rounded-lg ${game.winner === 'blue' ? 'bg-blue-100 border-2 border-blue-300' : 'bg-white'}`}>
              <div className="text-sm font-medium text-blue-600 mb-1">BLUE</div>
              <div className="text-4xl font-bold text-gray-900">{game.final_blue_score}</div>
              {game.participants && (
                <div className="text-xs text-gray-600 mt-2">
                  {getParticipantsByTeam('blue')}
                </div>
              )}
            </div>
          </div>

          {/* Game Info */}
          <div className="text-center">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white text-gray-700 border">
              {game.game_type === '1v1' ? '1v1 Match' : '2v2 Match'}
            </span>
          </div>

          {/* Click to view */}
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">Click to view full game details →</p>
          </div>
        </div>
      </Link>
    </div>
  )
}

